import { type Valve } from "@eisox/api/core-api";
import dayjs from "@eisox/dayjs";
import { MECA_PROBLEMS_ENUM, VALVE_ERRORS_ENUM, VALVE_WARNINGS_ENUM } from "@eisox/valves";

import { m } from "~/paraglide/messages";
import type { EnhancedValve } from "~/types/valve.type";

type StatusHandlers<T extends string> = Record<T, (v: EnhancedValve, showDate: boolean) => string>;

/* ERRORS */
const getProblemDate = (v: Valve, problemName?: MECA_PROBLEMS_ENUM) => {
  return problemName
    ? dayjs(v.mecaProblems.find(mp => mp.name === problemName)?.updatedAt).format("L LT")
    : dayjs(v.updatedAt).format("L LT");
};

export const VALVE_ERRORS_TRANSLATION: StatusHandlers<VALVE_ERRORS_ENUM | MECA_PROBLEMS_ENUM> = {
  [VALVE_ERRORS_ENUM.LONG_TIME_DISCONNECTED]: (v, showDate) =>
    m.disconnected({ since: showDate ? "true" : "false", date: getProblemDate(v) }),
  [VALVE_ERRORS_ENUM.DISCONNECTED]: (v, showDate) =>
    m.disconnected({ since: showDate ? "true" : "false", date: getProblemDate(v) }),
  [VALVE_ERRORS_ENUM.DISCONNECTED_GATEWAY]: () => m.valve_gateway_disconnected(),
  [MECA_PROBLEMS_ENUM.MOTOR_PROBLEM]: (v, showDate) =>
    m.motor_incident({ since: showDate ? "true" : "false", date: getProblemDate(v, MECA_PROBLEMS_ENUM.MOTOR_PROBLEM) }),
  [MECA_PROBLEMS_ENUM.ENCODER_PROBLEM]: (v, showDate) =>
    m.motor_encoder_incident({
      since: showDate ? "true" : "false",
      date: getProblemDate(v, MECA_PROBLEMS_ENUM.ENCODER_PROBLEM),
    }),
  [MECA_PROBLEMS_ENUM.INA_PROBLEM]: (v, showDate) =>
    m.motor_current_detection_incident({
      since: showDate ? "true" : "false",
      date: getProblemDate(v, MECA_PROBLEMS_ENUM.INA_PROBLEM),
    }),
  [MECA_PROBLEMS_ENUM.PLUGGED_FALSE]: (v, showDate) =>
    m.valve_not_on_body({
      since: showDate ? "true" : "false",
      date: getProblemDate(v, MECA_PROBLEMS_ENUM.PLUGGED_FALSE),
    }),
  [MECA_PROBLEMS_ENUM.VALVE_IS_SEIZED_UP]: (v, showDate) =>
    m.valve_body_seized({
      since: showDate ? "true" : "false",
      date: getProblemDate(v, MECA_PROBLEMS_ENUM.VALVE_IS_SEIZED_UP),
    }),
  [MECA_PROBLEMS_ENUM.NO_MOVE_AT_LOW_SPEED]: (v, showDate) =>
    m.motor_too_slow({
      since: showDate ? "true" : "false",
      date: getProblemDate(v, MECA_PROBLEMS_ENUM.NO_MOVE_AT_LOW_SPEED),
    }),
  [MECA_PROBLEMS_ENUM.NO_MAX_INT_CLOSE]: (v, showDate) =>
    m.no_intensity_on_closure({
      since: showDate ? "true" : "false",
      date: getProblemDate(v, MECA_PROBLEMS_ENUM.NO_MAX_INT_CLOSE),
    }),
  [MECA_PROBLEMS_ENUM.NO_MAX_INT]: (v, showDate) =>
    m.no_intensity_during_teach_in({
      since: showDate ? "true" : "false",
      date: getProblemDate(v, MECA_PROBLEMS_ENUM.NO_MAX_INT),
    }),
  [MECA_PROBLEMS_ENUM.UNSCREWED]: (v, showDate) =>
    m.valve_head_loose({ since: showDate ? "true" : "false", date: getProblemDate(v, MECA_PROBLEMS_ENUM.UNSCREWED) }),
  [MECA_PROBLEMS_ENUM.FAIL_TO_CLOSE]: (v, showDate) =>
    m.valve_fail_to_close({
      since: showDate ? "true" : "false",
      date: getProblemDate(v, MECA_PROBLEMS_ENUM.FAIL_TO_CLOSE),
    }),
};

/* WARNINGS */
export const VALVE_WARNINGS_TRANSLATION: StatusHandlers<VALVE_WARNINGS_ENUM> = {
  [VALVE_WARNINGS_ENUM.BAD_RSSI]: () => m.low_signal_strength(),
  [VALVE_WARNINGS_ENUM.BAD_LQI]: () => m.weak_signal_quality(),
  [VALVE_WARNINGS_ENUM.LOW_ROW_STROKE]: () => m.short_rod_stroke(),
  [VALVE_WARNINGS_ENUM.NO_LEARNING]: () => m.rod_stroke_learning_in_progress(),
  [VALVE_WARNINGS_ENUM.NOT_ACTIVATED]: () => m.not_activated(),
  [VALVE_WARNINGS_ENUM.DISCONNECTED_WARNING]: (v, showDate) =>
    m.valve_unavailable({
      since: showDate ? "true" : "false",
      date: dayjs(v.updatedAt).format("L LT"),
    }),
  [VALVE_WARNINGS_ENUM.LOW_BATTERY]: () => m.low_battery(),
  [VALVE_WARNINGS_ENUM.NOT_INSTALL]: () => m.in_pre_installation(),
};

/**
 * Retrieves the translations for the errors and warnings of a valve.
 * @param valve - The valve object containing the errors and warnings.
 * @returns An object containing the translated errors and warnings.
 */
export const getValveProblemTranslation = (valve: EnhancedValve) => {
  const errors = valve.errors.map(error => VALVE_ERRORS_TRANSLATION[error](valve, true));
  const warnings = valve.warnings.map(warning => VALVE_WARNINGS_TRANSLATION[warning](valve, true));
  return { errors, warnings };
};
